import React, { useState, useRef, useCallback } from 'react';
import ScrollX from '../components/ScrollX';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';

const CAD_TYPES = { harris: 'Harris CAD', travis: 'Travis CAD', cameron: 'Cameron CAD', other: 'Other CAD' };

const BADGE_COLORS = {
  'Harris CAD':  { bg: 'rgba(96,165,250,0.12)',  color: '#3b82f6', border: 'rgba(96,165,250,0.3)'  },
  'Travis CAD':  { bg: 'rgba(180,0,0,0.12)', color: 'var(--red-dark)', border: 'rgba(180,0,0,0.3)' },
  'Cameron CAD': { bg: 'rgba(167,139,250,0.12)', color: '#7c3aed', border: 'rgba(167,139,250,0.3)' },
  'Other CAD':   { bg: 'rgba(156,163,175,0.12)', color: '#6b7280', border: 'rgba(156,163,175,0.3)' },
};

const COLS = ['SysYear','Account','OwnerName','Address1','City','State','PostalCode','Assessor','AppealDate','Legal','CurrentLandFMV','CurrentImpFMV','MarketValue','CurrentAlternateValue'];

const PROMPT = `You are extracting data from Texas county appraisal district assessment notice PDFs for a property tax consulting firm.

IMPORTANT — DEDUPLICATION:
Some PDFs contain multiple copies of the same notice sent to different parties (owner, agent, consultant).
Each copy has the same Property ID but different mailing address.
You must extract ONLY ONE record per unique Property ID. Do not return duplicate Property IDs.

TASK: Extract the fields below for each unique property. Return ONLY valid JSON — no markdown, no explanation.

FIELD MAPPING:
- SysYear: Tax year (e.g. 2026)
- Account: The assessor's Property ID / account number for this parcel
- OwnerName: Legal owner name (property owner, not agent/consultant)
- Address1: Property situs/physical address — street and number only
- City: Property situs city
- State: "TX"
- PostalCode: Property situs ZIP code
- Assessor: Full name of the appraisal district
- AppealDate: Protest deadline in MM/DD/YYYY. Harris CAD = notice date + 30 days. Travis/Cameron = May 15.
- Legal: Legal description
- CurrentLandFMV: Current year land market value (number only, no $ or commas)
- CurrentImpFMV: Current year improvement/building market value (number only)
- MarketValue: Current year total market value (number only)
- CurrentAlternateValue: Net Appraised Value after any circuit breaker or homestead cap (number only)

CAD DETECTION:
- Harris CAD: "Harris Central Appraisal District" — long numeric accounts — appeal = notice date + 30 days
- Travis CAD: "Travis Central Appraisal District" — short Property IDs — appeal May 15
- Cameron CAD: "Cameron Appraisal District" — short Property IDs — "PROTEST DEADLINE: May 15"

JURISDICTION RULE: If all taxing units show the same taxable value, use that single value. Only split if values differ.
CIRCUIT BREAKER: If Net Appraised Value differs from Total Market Value, use Net Appraised Value for CurrentAlternateValue.

Return a JSON ARRAY (even for one property):
[{
  "SysYear":"","Account":"","OwnerName":"","Address1":"","City":"","State":"TX",
  "PostalCode":"","Assessor":"","AppealDate":"","Legal":"",
  "CurrentLandFMV":"","CurrentImpFMV":"","MarketValue":"","CurrentAlternateValue":"",
  "cadType":"","noticeDate":"","confidence":"high|medium|low"
}]`;

function fmt(v) {
  const n = parseFloat(String(v || '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? (v || '—') : n.toLocaleString();
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

export default function NoticeExtractor({ darkMode, allClients }) {
  const [files, setFiles]         = useState([]);
  const [results, setResults]     = useState([]);
  const [running, setRunning]     = useState(false);
  const [progress, setProgress]   = useState({ pct: 0, msg: '' });
  const [dragOver, setDragOver]   = useState(false);
  const [loadClient, setLoadClient] = useState('');
  const [loading, setLoading]     = useState(false);
  const fileInputRef              = useRef(null);
  const resultsRef                = useRef([]);

  const isDark = darkMode;
  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', th: 'rgba(26,34,56,0.5)',
    rowHover: 'rgba(255,255,255,0.03)',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8', th: 'var(--line-soft)',
    rowHover: 'rgba(0,0,0,0.02)',
  };

  const s = {
    card:    { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '18px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH:   { padding: '13px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
    cardT:   { fontSize: '14px', fontWeight: '700', color: c.text },
    btn:     { padding: '8px 16px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    btnBlue: { padding: '8px 16px', background: 'linear-gradient(135deg,#60a5fa,#3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    btnGreen:{ padding: '8px 16px', background: 'linear-gradient(135deg,#34d399,#10b981)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    th:      { padding: '9px 12px', textAlign: 'left', background: c.th, color: c.muted, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: `1px solid ${c.border}` },
    td:      { padding: '9px 12px', borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.3)' : '#eef0f6'}`, fontSize: '12px', color: c.text, verticalAlign: 'middle' },
  };

  // ── File handling ─────────────────────────────────────────────────────────
  function toBase64(file) {
    return new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.readAsDataURL(file); });
  }

  const addFiles = useCallback(async (rawFiles) => {
    const newEntries = [];
    for (const f of rawFiles) {
      if (!f.name.toLowerCase().endsWith('.pdf')) continue;
      if (files.find(x => x.name === f.name)) continue;
      const base64 = await toBase64(f);
      newEntries.push({ name: f.name, base64, status: 'pending', cadType: null, records: [] });
    }
    setFiles(prev => [...prev, ...newEntries]);
  }, [files]);

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }

  function clearAll() { setFiles([]); setResults([]); resultsRef.current = []; setProgress({ pct: 0, msg: '' }); }

  // ── Load from file_exchange via signed URLs — no local download needed ────
  async function loadFromFileExchange() {
    if (!loadClient) return;
    setLoading(true);
    setProgress({ pct: 0, msg: 'Loading files from File Exchange...' });
    try {
      // Get all upload tokens for this client
      const { data: tokens } = await supabase
        .from('upload_tokens')
        .select('token')
        .eq('client_id', loadClient)
        .gt('files_received', 0)
        .order('created_at', { ascending: false });

      if (!tokens?.length) {
        setProgress({ pct: 0, msg: 'No upload tokens found for this client.' });
        setLoading(false);
        return;
      }

      const tokenList = tokens.map(t => t.token);

      // Query file_exchange BY TOKEN — not by client_id
      // This catches all files from that upload session even if
      // they were later reassigned to different owner clients
      const { data: fxFiles, error } = await supabase
        .from('file_exchange')
        .select('id, file_name, storage_path, folder_name, upload_token, client_id')
        .in('upload_token', tokenList)
        .eq('direction', 'client_to_ntrg')
        .is('split_at', null)   // a split original's pages are its content; reading both would extract it twice
        .order('file_name');

      if (error) throw new Error(error.message);
      if (!fxFiles?.length) {
        setProgress({ pct: 0, msg: 'No files found for this client\'s upload sessions.' });
        setLoading(false);
        return;
      }

      const pdfs = fxFiles.filter(f => f.file_name?.toLowerCase().endsWith('.pdf'));
      let loaded = 0;

      for (const fx of pdfs) {
        if (files.find(x => x.name === fx.file_name)) continue;
        if (!fx.storage_path) continue;

        setProgress({ pct: Math.round((loaded / pdfs.length) * 100), msg: `Loading ${loaded + 1} of ${pdfs.length}: ${fx.file_name}` });

        let sdUrl = null; try { sdUrl = await fileUrl(fx.storage_path, 'extract'); } catch { sdUrl = null; }

        if (!sdUrl) continue;

        const resp = await fetch(sdUrl);
        if (!resp.ok) continue;
        const blob = await resp.blob();
        const base64 = await new Promise(res => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });

        setFiles(prev => [...prev, { name: fx.file_name, base64, status: 'pending', cadType: null, records: [], fxId: fx.id }]);
        loaded++;
      }

      setProgress({ pct: 100, msg: `✓ Loaded ${loaded} file${loaded !== 1 ? 's' : ''} — ready to extract` });
    } catch (e) {
      setProgress({ pct: 0, msg: `Error: ${e.message}` });
    }
    setLoading(false);
  }

  // ── Extract ────────────────────────────────────────────────────────────────
  async function extractOne(base64, fileName, retries = 2) {
    try {
      const resp = await fetch('/api/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64, docType: 'multi_cad_notice', prompt: PROMPT }),
      });

      if (resp.status === 429) {
        if (retries > 0) { await sleep(30000); return extractOne(base64, fileName, retries - 1); }
        throw new Error('Rate limited — try again in a minute');
      }
      // ⛔ THE BODY CARRIES THE REASON AND THIS USED TO DISCARD IT. api/extract.js now answers 422 with a
      // distinct message — "Extraction truncated: document too long" vs "Parse failed" — and this path is the
      // likeliest to see it: multi_cad_notice returns an ARRAY of parcels, so a long PDF runs out of tokens
      // before a single-parcel notice does. "API error 422" named neither.
      if (!resp.ok) {
        const raw = await resp.text().catch(() => '');
        let reason = '';
        try { reason = (JSON.parse(raw) || {}).error || ''; } catch { reason = raw.slice(0, 120); }
        throw new Error(reason || `API error ${resp.status}`);
      }

      const data = await resp.json();
      const rawText = (data.content?.[0]?.text || data.text || '').replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(rawText);
      const arr = Array.isArray(parsed) ? parsed : [parsed];

      // Deduplicate by Account within this PDF
      const seen = new Set();
      return arr.filter(r => {
        if (!r.Account) return true;
        if (seen.has(r.Account)) return false;
        seen.add(r.Account); return true;
      });
    } catch (e) {
      if (retries > 0 && e.message.includes('JSON')) {
        await sleep(2000); return extractOne(base64, fileName, retries - 1);
      }
      throw e;
    }
  }

  async function runExtraction() {
    setRunning(true);
    resultsRef.current = [];
    setResults([]);

    const pending = files.filter(f => f.status !== 'done');
    let done = files.filter(f => f.status === 'done').length;

    for (let i = 0; i < pending.length; i++) {
      const f = pending[i];
      const pct = Math.round((i / pending.length) * 100);
      setProgress({ pct, msg: `Processing ${i + 1} of ${pending.length}: ${f.name}` });

      // Mark processing
      setFiles(prev => prev.map(x => x.name === f.name ? { ...x, status: 'processing' } : x));

      try {
        const records = await extractOne(f.base64, f.name);
        resultsRef.current = [...resultsRef.current, ...records];
        setResults([...resultsRef.current]);
        setFiles(prev => prev.map(x => x.name === f.name ? { ...x, status: 'done', cadType: records[0]?.cadType || 'Other CAD', records } : x));
        done++;
      } catch (e) {
        console.error(f.name, e);
        setFiles(prev => prev.map(x => x.name === f.name ? { ...x, status: 'error', error: e.message } : x));
      }

      if (i < pending.length - 1) await sleep(8000);
    }

    setProgress({ pct: 100, msg: `✓ Complete — ${resultsRef.current.length} record${resultsRef.current.length !== 1 ? 's' : ''} extracted` });
    setRunning(false);
  }

  // ── Download CSV ───────────────────────────────────────────────────────────
  function downloadCSV() {
    const good = results.filter(r => r && r.Account);
    const rows = [COLS.join(',')];
    good.forEach(r => rows.push(COLS.map(col => `"${String(r[col] || '').replace(/"/g, '""')}"`).join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `NTRG_Assessment_Notices_${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
  }

  const statDone    = files.filter(f => f.status === 'done').length;
  const statErrors  = files.filter(f => f.status === 'error').length;
  const statPending = files.filter(f => f.status === 'pending' || f.status === 'processing').length;
  const goodResults = results.filter(r => r && r.Account);

  return (
    <div style={{ maxWidth: '1200px', fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
      {/* Header */}
      <div style={{ marginBottom: '22px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>
          Notice Extractor
        </div>
        <div style={{ fontSize: '13px', color: c.muted }}>
          Extract values from assessment notices for Siger Tax import. Harris · Travis · Cameron CAD auto-detected.
          <span style={{ marginLeft: '12px', background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '600' }}>
            ✓ No data saved to WigleyHub
          </span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Files Loaded',  value: files.length,  color: c.text },
          { label: 'Processed',     value: statDone,       color: '#34d399' },
          { label: 'Errors',        value: statErrors,     color: '#f87171' },
          { label: 'Records Found', value: goodResults.length, color: 'var(--red)' },
        ].map(st => (
          <div key={st.label} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '14px 18px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '11px', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>{st.label}</div>
            <div style={{ fontSize: '26px', fontWeight: '700', color: st.color, fontFamily: "'Archivo',sans-serif" }}>{st.value}</div>
          </div>
        ))}
      </div>

      {/* Upload card */}
      <div style={s.card}>
        <div style={s.cardH}>
          <div style={s.cardT}>1 · Load PDF Files</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {files.length > 0 && !running && (
              <button style={{ ...s.btn, background: 'transparent', color: c.muted, border: `1px solid ${c.border}` }} onClick={clearAll}>✕ Clear All</button>
            )}
            <button style={{ ...s.btn, opacity: (running || files.filter(f => f.status !== 'done').length === 0) ? 0.4 : 1 }}
              disabled={running || files.filter(f => f.status !== 'done').length === 0}
              onClick={runExtraction}>
              {running ? '⏳ Extracting...' : '⚡ Extract All Files'}
            </button>
            <button style={{ ...s.btnGreen, opacity: goodResults.length === 0 ? 0.4 : 1 }}
              disabled={goodResults.length === 0} onClick={downloadCSV}>
              ⬇ Download CSV
            </button>
          </div>
        </div>

        {/* Load from File Exchange — no downloading needed */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}`, background: isDark ? 'rgba(96,165,250,0.04)' : 'rgba(96,165,250,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
            📥 Load directly from File Exchange — no downloading required
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              style={{ ...s.select || {}, flex: 1, maxWidth: '360px', padding: '8px 12px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" }}
              value={loadClient}
              onChange={e => setLoadClient(e.target.value)}>
              <option value="">— Select client to load their incoming files —</option>
              {(allClients || []).map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
            </select>
            <button
              style={{ ...s.btnBlue, opacity: (!loadClient || loading) ? 0.4 : 1 }}
              disabled={!loadClient || loading}
              onClick={loadFromFileExchange}>
              {loading ? '⏳ Loading...' : '📥 Load Files'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: c.muted, marginTop: '8px' }}>
            Loads all unprocessed incoming PDFs for the selected client directly from storage — staff never need to download files locally.
          </div>
        </div>

        {/* Drop zone */}
        <div style={{ padding: '16px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--red)' : c.inputBorder}`,
              borderRadius: '10px', padding: '36px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'rgba(180,0,0,0.04)' : 'transparent', transition: 'all 0.15s',
            }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>📄</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: c.text, marginBottom: '4px' }}>
              Drop assessment notice PDFs here or click to browse
            </div>
            <div style={{ fontSize: '12px', color: c.muted }}>
              Harris CAD · Travis CAD · Cameron CAD — auto-detected · Multi-copy PDFs deduplicated
            </div>
            <input ref={fileInputRef} type="file" multiple accept=".pdf" style={{ display: 'none' }}
              onChange={e => addFiles(Array.from(e.target.files))} />
          </div>

          {/* Progress */}
          {(running || progress.msg) && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ background: isDark ? '#374060' : '#eef0f6', borderRadius: '4px', height: '6px', overflow: 'hidden', marginBottom: '8px' }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg,#9C0000,#B40000)', borderRadius: '4px', width: `${progress.pct}%`, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: '12px', color: c.muted }}>{progress.msg}</div>
            </div>
          )}
        </div>

        {/* Note */}
        <div style={{ padding: '10px 18px', borderTop: `1px solid ${c.border}`, fontSize: '11px', color: c.muted, background: isDark ? 'rgba(0,0,0,0.1)' : '#f8f9fc' }}>
          ℹ️ This tool extracts values for Siger Tax CSV export only. Nothing is saved to WigleyHub, Supabase, or any parcel record.
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={s.card}>
          <div style={s.cardH}>
            <div style={s.cardT}>Files</div>
            <div style={{ fontSize: '12px', color: c.muted }}>{files.length} file{files.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {files.map((f, i) => {
              const badgeStyle = f.cadType ? BADGE_COLORS[f.cadType] || BADGE_COLORS['Other CAD'] : null;
              const statusColor = f.status === 'done' ? '#34d399' : f.status === 'error' ? '#f87171' : f.status === 'processing' ? '#60a5fa' : c.muted;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.3)' : '#eef0f6'}` }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: `${statusColor}20`, color: statusColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0, animation: f.status === 'processing' ? 'pulse 1s infinite' : 'none' }}>
                    {f.status === 'pending' ? '○' : f.status === 'processing' ? '⟳' : f.status === 'done' ? '✓' : '✗'}
                  </div>
                  <div style={{ flex: 1, fontSize: '13px', fontWeight: '500', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                  {f.cadType && badgeStyle && (
                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: badgeStyle.bg, color: badgeStyle.color, border: `1px solid ${badgeStyle.border}`, flexShrink: 0 }}>
                      {f.cadType}
                    </span>
                  )}
                  {f.status === 'done' && f.records?.length > 0 && (
                    <span style={{ fontSize: '11px', color: '#34d399', flexShrink: 0 }}>{f.records.length} record{f.records.length !== 1 ? 's' : ''}</span>
                  )}
                  {f.status === 'error' && (
                    <span style={{ fontSize: '11px', color: '#f87171', flexShrink: 0 }}>Failed</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Results table */}
      {goodResults.length > 0 && (
        <div style={s.card}>
          <div style={s.cardH}>
            <div style={s.cardT}>2 · Extracted Data — Ready for Siger Tax</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: c.muted }}>{goodResults.length} unique record{goodResults.length !== 1 ? 's' : ''}</span>
              <button style={s.btnGreen} onClick={downloadCSV}>⬇ Download CSV</button>
            </div>
          </div>
          <ScrollX style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>CAD</th>
                  <th style={s.th}>Year</th>
                  <th style={s.th}>Account</th>
                  <th style={s.th}>Owner</th>
                  <th style={s.th}>Address</th>
                  <th style={s.th}>City</th>
                  <th style={s.th}>ZIP</th>
                  <th style={s.th}>Appeal Date</th>
                  <th style={s.th}>Land FMV</th>
                  <th style={s.th}>Imp FMV</th>
                  <th style={s.th}>Market Value</th>
                  <th style={s.th}>Alt Value</th>
                  <th style={s.th}>Conf</th>
                </tr>
              </thead>
              <tbody>
                {goodResults.map((r, i) => {
                  const badgeStyle = BADGE_COLORS[r.cadType] || BADGE_COLORS['Other CAD'];
                  const confColor = r.confidence === 'high' ? '#34d399' : r.confidence === 'medium' ? '#fbbf24' : '#f87171';
                  return (
                    <tr key={i} onMouseEnter={e => e.currentTarget.style.background = c.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...s.td, color: c.muted, fontSize: '11px' }}>{i + 1}</td>
                      <td style={s.td}>
                        <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', background: badgeStyle.bg, color: badgeStyle.color, border: `1px solid ${badgeStyle.border}`, whiteSpace: 'nowrap' }}>
                          {r.cadType || '?'}
                        </span>
                      </td>
                      <td style={s.td}>{r.SysYear || ''}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '11px' }}>{r.Account || ''}</td>
                      <td style={{ ...s.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.OwnerName || ''}</td>
                      <td style={{ ...s.td, fontSize: '11px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.Address1 || ''}</td>
                      <td style={{ ...s.td, fontSize: '11px' }}>{r.City || ''}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '11px' }}>{r.PostalCode || ''}</td>
                      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{r.AppealDate || ''}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>{fmt(r.CurrentLandFMV)}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>{fmt(r.CurrentImpFMV)}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}>{fmt(r.MarketValue)}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' }}>{fmt(r.CurrentAlternateValue)}</td>
                      <td style={{ ...s.td, color: confColor, fontSize: '11px', fontWeight: '600' }}>{r.confidence || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollX>
        </div>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
    </div>
  );
}
